<template>
  <Layout>
    <!-- Main Content -->

    <!-- Main Content -->
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <!-- Hero Section -->
      <div class="text-center mb-12">
        <h2 class="text-4xl font-bold text-gray-900 mb-4">
          {{ $t('contact.title') }}
        </h2>
        <p class="text-xl text-gray-600 max-w-2xl mx-auto">
          {{ $t('contact.subtitle') }}
        </p>
      </div>

      <!-- Contact Form -->
      <div class="bg-white rounded-lg shadow-lg p-8">
        <form @submit.prevent="submitForm" class="space-y-6">
          <!-- Success Message -->
          <div v-if="successMessage" class="bg-green-50 border border-green-200 rounded-lg p-4">
            <div class="flex items-center">
              <svg class="w-5 h-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <p class="text-green-800 font-medium">{{ successMessage }}</p>
            </div>
          </div>

          <!-- Error Message -->
          <div v-if="errorMessage" class="bg-red-50 border border-red-200 rounded-lg p-4">
            <div class="flex items-center">
              <svg class="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
              <p class="text-red-800 font-medium">{{ errorMessage }}</p>
            </div>
          </div>

          <!-- Name and Email Row -->
          <div class="grid md:grid-cols-2 gap-6">
            <div>
              <label for="name" class="block text-sm font-medium text-gray-700 mb-2">
                {{ $t('contact.form.name') }}
              </label>
              <input
                id="name"
                v-model="formData.name"
                type="text"
                required
                :class="[
                  'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500',
                  errors.name ? 'border-red-300' : 'border-gray-300'
                ]"
                :placeholder="$t('contact.form.namePlaceholder')"
              />
              <p v-if="errors.name" class="mt-1 text-sm text-red-600">{{ errors.name }}</p>
            </div>

            <div>
              <label for="email" class="block text-sm font-medium text-gray-700 mb-2">
                {{ $t('contact.form.email') }}
              </label>
              <input
                id="email"
                v-model="formData.email"
                type="email"
                required
                :class="[
                  'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500',
                  errors.email ? 'border-red-300' : 'border-gray-300'
                ]"
                :placeholder="$t('contact.form.emailPlaceholder')"
              />
              <p v-if="errors.email" class="mt-1 text-sm text-red-600">{{ errors.email }}</p>
            </div>
          </div>

          <!-- Company and Phone Row -->
          <div class="grid md:grid-cols-2 gap-6">
            <div>
              <label for="company" class="block text-sm font-medium text-gray-700 mb-2">
                {{ $t('contact.form.company') }}
              </label>
              <input
                id="company"
                v-model="formData.company"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                :placeholder="$t('contact.form.companyPlaceholder')"
              />
            </div>

            <div>
              <label for="phone" class="block text-sm font-medium text-gray-700 mb-2">
                {{ $t('contact.form.phone') }}
              </label>
              <input
                id="phone"
                v-model="formData.phone"
                type="tel"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                :placeholder="$t('contact.form.phonePlaceholder')"
              />
            </div>
          </div>

          <!-- Subject -->
          <div>
            <label for="subject" class="block text-sm font-medium text-gray-700 mb-2">
              {{ $t('contact.form.subject') }}
            </label>
            <input
              id="subject"
              v-model="formData.subject"
              type="text"
              required
              :class="[
                'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500',
                errors.subject ? 'border-red-300' : 'border-gray-300'
              ]"
              :placeholder="$t('contact.form.subjectPlaceholder')"
            />
            <p v-if="errors.subject" class="mt-1 text-sm text-red-600">{{ errors.subject }}</p>
          </div>

          <!-- Message -->
          <div>
            <label for="message" class="block text-sm font-medium text-gray-700 mb-2">
              {{ $t('contact.form.message') }}
            </label>
            <textarea
              id="message"
              v-model="formData.message"
              rows="6"
              required
              :class="[
                'w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none',
                errors.message ? 'border-red-300' : 'border-gray-300'
              ]"
              :placeholder="$t('contact.form.messagePlaceholder')"
            ></textarea>
            <p v-if="errors.message" class="mt-1 text-sm text-red-600">{{ errors.message }}</p>
            <p class="mt-1 text-sm text-gray-500">
              {{ formData.message.length }}/1000 {{ $t('contact.form.characters') }}
            </p>
          </div>

          <!-- Submit Button -->
          <div class="flex justify-center">
            <button
              type="submit"
              :disabled="isSubmitting"
              class="bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-medium py-3 px-8 rounded-lg transition-colors duration-200 flex items-center"
            >
              <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ isSubmitting ? $t('contact.form.sending') : $t('contact.form.submit') }}
            </button>
          </div>
        </form>
      </div>

      <!-- Contact Information -->
      <div class="mt-12 grid md:grid-cols-3 gap-8">
        <div class="text-center">
          <div class="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <svg class="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ $t('contact.info.email.title') }}</h3>
          <p class="text-gray-600">{{ $t('contact.info.email.value') }}</p>
        </div>

        <div class="text-center">
          <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ $t('contact.info.location.title') }}</h3>
          <p class="text-gray-600">{{ $t('contact.info.location.value') }}</p>
        </div>

        <div class="text-center">
          <div class="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <svg class="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ $t('contact.info.response.title') }}</h3>
          <p class="text-gray-600">{{ $t('contact.info.response.value') }}</p>
        </div>
      </div>

      <!-- RGPD Notice -->
      <div class="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div class="flex items-start">
          <svg class="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
          </svg>
          <div>
            <h4 class="text-sm font-medium text-blue-900 mb-1">{{ $t('contact.rgpd.title') }}</h4>
            <p class="text-sm text-blue-700">
              {{ $t('contact.rgpd.description') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import axios from '../config/axios';
import Layout from './Layout.vue';

export default {
  name: 'ContactPage',
  components: {
    Layout
  },
  data() {
    return {
      formData: {
        name: '',
        email: '',
        company: '',
        phone: '',
        subject: '',
        message: ''
      },
      errors: {},
      isSubmitting: false,
      successMessage: '',
      errorMessage: ''
    };
  },
  methods: {
    validateForm() {
      this.errors = {};

      if (!this.formData.name.trim()) {
        this.errors.name = this.$t('contact.validation.nameRequired');
      } else if (this.formData.name.trim().length < 2) {
        this.errors.name = this.$t('contact.validation.nameMinLength');
      }

      if (!this.formData.email.trim()) {
        this.errors.email = this.$t('contact.validation.emailRequired');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.formData.email)) {
        this.errors.email = this.$t('contact.validation.emailInvalid');
      }

      if (!this.formData.subject.trim()) {
        this.errors.subject = this.$t('contact.validation.subjectRequired');
      } else if (this.formData.subject.trim().length < 5) {
        this.errors.subject = this.$t('contact.validation.subjectMinLength');
      }

      if (!this.formData.message.trim()) {
        this.errors.message = this.$t('contact.validation.messageRequired');
      } else if (this.formData.message.trim().length < 10) {
        this.errors.message = this.$t('contact.validation.messageMinLength');
      } else if (this.formData.message.length > 1000) {
        this.errors.message = this.$t('contact.validation.messageMaxLength');
      }

      return Object.keys(this.errors).length === 0;
    },

    async submitForm() {
      if (!this.validateForm()) {
        return;
      }

      this.isSubmitting = true;
      this.successMessage = '';
      this.errorMessage = '';

      try {
        const response = await axios.post('/contact/send', this.formData);
        
        if (response.data.success) {
          this.successMessage = this.$t('contact.form.success');
          // Reset form
          this.formData = {
            name: '',
            email: '',
            company: '',
            phone: '',
            subject: '',
            message: ''
          };
        } else {
          this.errorMessage = response.data.error || 'Une erreur est survenue';
        }
      } catch (error) {
        console.error('Contact form error:', error);
        
        if (error.response?.data?.details) {
          // Validation errors from backend
          this.errors = {};
          error.response.data.details.forEach(errorMsg => {
            if (errorMsg.includes('nom')) this.errors.name = errorMsg;
            else if (errorMsg.includes('email')) this.errors.email = errorMsg;
            else if (errorMsg.includes('sujet')) this.errors.subject = errorMsg;
            else if (errorMsg.includes('message')) this.errors.message = errorMsg;
          });
        } else {
          this.errorMessage = error.response?.data?.error || this.$t('contact.form.error');
        }
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script> 